/**
 * ViewPerCurrency Component
 *
 * This component renders a table footer with data specific to a given column.
 * It displays either a sum of currency data or a button to close the preview, depending on the type of column.
 * The component uses `useLayoutEffect` to calculate the height of the dialog and updates the store with relevant data.
 *
 * Props:
 *  - `column` (string): The name of the column. This prop determines the behavior and rendering of the component.
 *  - `isLastColumn` (boolean): A flag to determine if the column is the last one in the table. It helps in applying styling (e.g., borders).
 *  - `className` (string): Additional CSS class to style the footer. This can be used to control visibility and layout.
 *
 * State/Refs:
 *  - `viewPerCcyRef` (RefObject<HTMLDivElement>): Reference to the div wrapping the footer content. This is used for dynamic height calculation.
 *  - `rowDensityClass`, `rowDensityHeight`: These are derived from the custom `useRowDensity` hook and control the row styling and height.
 *  - `geoColumnHeight`, `locationColumnHeight`: Heights fetched from the Redux store, used to adjust the padding and layout for geo/location columns.
 *
 * Effect:
 *  - `useLayoutEffect`: Calculates the height of the footer dialog based on the number of data rows and updates the store accordingly.
 *
 * Logic:
 *  - **Location Column**: If the column is a "location" column, the component renders a button that allows the user to close the currency preview.
 *  - **Currency Column**: If the column is related to currency (CCY), the component displays the sum of values with the respective currency flag.
 *  - **Other Columns**: For any other columns, it renders a row with data, formatted based on column properties like border styles and row density.
 *
 * Conditional Rendering:
 *  - The component skips rendering for certain geo columns excluding the location column. This is handled by the utility function `isGeoColumnExcludingLocation`.
 *
 * Example Usage:
 *  <ViewPerCurrency
 *    column="CCY"
 *    isLastColumn={true}
 *    className="visible_footer"
 *  />
 */

import React, { useLayoutEffect, useRef } from "react";
import { t } from "i18next";
import { GeoColumns } from "../../../../../enum/GeoDivisionColumnName";
import { StatsTableColumn } from "../../../../../enum/TableColumn";
import {
  borderStyle,
  getTableClass,
  isGeoColumnExcludingLocation,
} from "../../../../../util/table-helper";
import { useViewPerCurrency } from "../../../../../hook/useViewPerCurrency";
import { useRowDensity } from "../../../../../hook/useRowDensity";
import {
  setShouldViewPerCurrency,
  setViewPerCcyDialogHeight,
} from "../../../../../store/slice/stats-slice";
import store from "../../../../../store/store";
import Button from "../../Button";
import { getCurrencyIcon } from "../../../../../util/currency";
import FooterWrapper from "./FooterWrapper";

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  className: string;
}

const ViewPerCurrency: React.FC<TableFooterProps> = ({
  column,
  isLastColumn,
  className,
}) => {
  // Fetch currency data using the custom hook `useViewPerCurrency`
  const data = useViewPerCurrency(column);
  const { rowDensityClass, rowDensityHeight } = useRowDensity();
  const viewPerCcyRef = useRef<HTMLDivElement>(null);

  const isLocationColumn = column === GeoColumns.LOCATION; // Check if the column is for location
  const isCCYColumn = column === StatsTableColumn.CCY; // Check if the column is for currency
  const geoColumnHeight = store.getState().stats.geoColumnHeight ?? 0; // Get geo column height from store
  const locationColumnHeight = store.getState().stats.locationColumnHeight ?? 0; // Get location column height from store

  // Handle closing the currency preview
  const handleClosePreview = () => store.dispatch(setShouldViewPerCurrency());

  useLayoutEffect(() => {
    // Update dialog height when data or row density changes
    if (viewPerCcyRef.current && data.length > 0) {
      store.dispatch(
        setViewPerCcyDialogHeight({
          numberOfRecords: data.length,
          rowDensityHeight,
        })
      );
    }
  }, [column, data, rowDensityHeight]);

  // Skip rendering for geo columns excluding LOCATION
  if (isGeoColumnExcludingLocation(column)) {
    return (
      <FooterWrapper
        column={column}
        className={className}
        height={className === "hidden_footer" ? 0 : geoColumnHeight}
      >
        {null}
      </FooterWrapper>
    );
  }

  return (
    <FooterWrapper
      column={column}
      className={className}
      isCCYColumn={isCCYColumn}
      isLocationColumn={isLocationColumn}
      refProp={viewPerCcyRef}
    >
      {/* Render data rows for non-location columns */}
      {!isLocationColumn &&
        data.map((cellData, idx) => (
          <div
            key={idx}
            className={`table_row ${getTableClass(column, "footer_row")}`}
            style={borderStyle({
              idx,
              dataLength: data.length,
              isLastColumn,
              isCCYColumn,
            })}
          >
            <div
              className={`table_cell ${rowDensityClass}`}
              style={{ justifyContent: !isCCYColumn ? "end" : "start" }}
            >
              {isCCYColumn && "Σ (SUM)"}{" "}
              {isCCYColumn && (
                <img
                  className="ccy_flag"
                  src={getCurrencyIcon(cellData)}
                  alt={`ccy_${cellData}`}
                />
              )}{" "}
              {cellData}
            </div>
          </div>
        ))}

      {/* Render button only for LOCATION column */}
      {isLocationColumn && (
        <div
          className={`table_row ${getTableClass(
            column,
            "footer_row"
          )} location_column`}
          style={{
            paddingTop: `${
              className === "hidden_footer" ? 0 : locationColumnHeight
            }px`,
          }}
        >
          <Button
            className={`button view_per_currency ${rowDensityClass}`}
            onClick={handleClosePreview}
          >
            {t("statisticPage.table.buttons.closePreview")}
          </Button>
        </div>
      )}
    </FooterWrapper>
  );
};

export default ViewPerCurrency;
